import type Route from '@ember/routing/route';
import { later, next, schedule } from '@ember/runloop';
import { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';

import WebFont from 'webfontloader';

import type {
	APIData,
	CreateRecordProperties,
	ImageCropData,
	PopupData,
	RecordData,
	RequestError,
	Service,
	SourcesMap,
	TemplateResolve,
} from 'webapp-types';
import { showTemplateCreationResult } from 'webapp/const/template-resolve';
import {
	BillingFeature,
	PopupContextSwitchAction as ContextSwitchAction,
	ModelName,
	NewReportSlideLayout,
	ReportLayout,
	TemplateObjectType,
} from 'webapp/enums/enums';
import type { ReportCoverAction as CoverAction, ReportPopupShareTab } from 'webapp/enums/enums';
import { NotificationCreateType } from 'webapp/enums/notifications';
import { TrackingEvent } from 'webapp/enums/tracking';
import { sortBy } from 'webapp/utils/common';

import ReportsPopupCoverComponent from 'webapp/components/reports/popup-cover';
import ReportsPopupCreateBlockComponent from 'webapp/components/reports/popup-create-block';
import ReportsPopupPreviewComponent from 'webapp/components/reports/popup-preview';
import ReportsPopupShareComponent from 'webapp/components/reports/popup-share';
import ReportsPopupTourIntroComponent from 'webapp/components/reports/popup-tour-intro';
import type ReportsEditorReportContoller from 'webapp/controllers/reports/editor/report';
import type ReportsViewReportController from 'webapp/controllers/reports/view/report';
import type ReportsViewReportRoute from 'webapp/routes/reports/view/report';

import type Block from 'webapp/models-custom/block';
import type Board from 'webapp/models-custom/board';
import type BoardLoop from 'webapp/models-custom/board-loop';
import type InfolayerItemGeneral from 'webapp/models-custom/infolayer-item/general';
import type PopupModal from 'webapp/models-custom/popup/modal';
import Report, { DEFAULT_REPORT_TITLE } from 'webapp/models-custom/report';
import type ReportSlide from 'webapp/models-custom/report-slide';
import type Template from 'webapp/models-custom/template';
import type BlockRecord from 'webapp/models/block';
import type ReportRecord from 'webapp/models/report';

import { Severity } from 'webapp/services/logger';
import type { TrackingOriginTypeForAddReport } from 'webapp/services/model-manager/boards';
import ModelManagerForStoreService from 'webapp/services/model-manager/for-store';
import type { TagCountList } from 'webapp/services/model-manager/tags';
import type { TrackingOriginForTemplateUsed } from 'webapp/services/model-manager/templates';

export type TrackingOriginTypeForCreate =
	'page' | 'home' | 'databoards' | 'designer' | 'reports' | 'loops' | 'sidebar';
export type TrackingOriginTypeForPreview =
	| 'page'
	| 'reports'
	| 'reports_scheduled'
	| 'home'
	| 'sidebar'
	| 'notifications'
	| 'popup_usages';
export type TrackingOriginTypeForShare =
	'page' | 'reports' | 'reports_scheduled' | 'editor' | 'home';
export type TrackingOriginTypeForSchedulePopup =
	TrackingOriginTypeForShare | 'share popup' | 'notifications';
export type TrackingOriginTypeForReportEdited = 'page' | 'reports' | 'editor';
export type TrackingOriginForTemplateCreated = 'home' | 'reports' | 'editor' | 'account_templates';
export type TrackingOriginForPresent =
	'reports' | 'reports_preview' | 'editor' | 'reports_public_URL';

interface RequestedReportData {
	id: string;
	requestedTimestamp: number;
}

export default class ModelManagerReportsService extends ModelManagerForStoreService<
	ReportRecord,
	Report
> {
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('billing') declare billingService: Service.Billing;
	@service('logger') declare loggerService: Service.Logger;
	@service('request') declare requestService: Service.Request;
	@service('garbage-collector') declare garbageCollectorService: Service.GarbageCollector;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('popup') declare popupService: Service.Popup;
	@service('socket') declare socketService: Service.Socket;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('navigation-main') declare navigationMainService: Service.NavigationMain;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('account') declare accountService: Service.Account;
	@service('model-manager/blocks') declare mmBlocksService: Service.ModelManager.Blocks;
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('model-manager/report-slides')
	declare mmReportSlidesService: Service.ModelManager.ReportSlides;
	@service('model-manager/report-deliveries')
	declare mmReportDeliveriesService: Service.ModelManager.ReportDeliveries;
	@service('model-manager/tags') declare mmTagsService: Service.ModelManager.Tags;
	@service('model-manager/templates') declare mmTemplatesService: Service.ModelManager.Templates;
	@service('model-manager/source-types')
	declare mmSourceTypesService: Service.ModelManager.SourceTypes;
	@service('model-manager/report-schedule-settings')
	declare mmReportScheduleSettingsService: Service.ModelManager.ReportScheduleSettings;
	@service('model-manager/notifications')
	declare mmNotificationsService: Service.ModelManager.Notifications;
	@service('verification') declare verificationService: Service.Verification;

	private _reportModels: Report[] = [];

	@tracked isFontLoaded = false;

	@cached
	get reportModels(): Report[] {
		this.updateModelCache(this._reportModels, this.savedRecords);
		return this._reportModels;
	}

	@cached
	get normalReportModels(): Report[] {
		return this.normalReportModelsWithDeleted.filter((report: Report) => !report.deleted);
	}

	@cached
	get normalReportModelsWithDeleted(): Report[] {
		// also includes public templates in the public template space
		return this.reportModels.filter((report: Report) => {
			return (
				!report.templateId ||
				(this.accountService.isPublictemplatesSpace && report.templateModel?.isPublic)
			);
		});
	}

	@cached
	get accountTemplateReportModels(): Report[] {
		return this.reportModels.filter((report: Report) => {
			const template = report.templateModel;

			return !!template && !template.isPublic;
		});
	}

	get assignedTags(): TagCountList[] {
		const tags = this.normalReportModels.reduce((acc: number[], model: Report) => {
			acc.push(...model.tags);
			return acc;
		}, []);
		return this.mmTagsService.getTagCountList(tags);
	}

	get assignedTagsForAccountTemplates(): TagCountList[] {
		const tags = this.accountTemplateReportModels.reduce((acc: number[], model: Report) => {
			acc.push(...model.tags);
			return acc;
		}, []);
		return this.mmTagsService.getTagCountList(tags);
	}

	startCreateNewFlow(origin: TrackingOriginTypeForCreate = 'page'): Promise<Report> {
		if (this.accountService.handleDemo()) {
			return Promise.reject();
		}

		this.analyticsService.event(TrackingEvent.REPORT_NEW, { origin });

		return new Promise((resolve, reject) => {
			const report = this.createNewModel({ title: DEFAULT_REPORT_TITLE });
			report
				.save()
				.then((reportRecord) => {
					this.analyticsService.event(TrackingEvent.REPORT_CREATED, { origin });

					const report = this.findModel(reportRecord.id!);
					if (report) {
						void this.mmReportSlidesService
							.createNewFromLayout(report, NewReportSlideLayout.COVER)
							.then(() => {
								this.editReport(report);
								resolve(report);
							});
					} else {
						throw new Error('No report');
					}
				})
				.catch(reject);
		});
	}

	toggleReportFavorite(report: Report): void {
		if (this.accountService.handleDemo()) {
			return;
		}

		const isNowFavorite = !report.favorite;
		report.favorite = isNowFavorite;

		const layer = this.infolayerService.loading('Saving...');
		// update order to maintain favorites at top
		this.updateOrderAfterFavoriteToggle()
			.then(() => report.save())
			.then(
				(report) => {
					layer.ok(
						`Report <strong>${report.title}</strong> ${isNowFavorite ? '' : 'un'}favorited${isNowFavorite ? ' and moved to the top' : ' and removed from the top'}!`,
					);
				},
				(errorData) => {
					layer.close();
					throw errorData;
				},
			)
			.catch(this.requestService.showAllValidationErrors.bind(this.requestService));

		if (isNowFavorite) {
			this.analyticsService.event(TrackingEvent.REPORT_FAVORITED, {
				name: report.title,
				datasources: [], // report.sourceTypeKeys
			});
		}
	}

	editReport(report: Report, selectedSlide?: ReportSlide): void {
		if (this.accountService.handleDemo()) {
			return;
		}

		this.navigationService
			.transitionToRoute('reports.editor.report', report.model)
			.then((route) => {
				if (selectedSlide && route) {
					((route as Route).controller as ReportsEditorReportContoller).selectedSlideId =
						selectedSlide.id;
				}
			});
	}

	openPreviewPopup(report: Report, origin: TrackingOriginTypeForPreview = 'page'): void {
		void this.openPreviewAndGetPopup(report, origin);
	}

	// openPreviewPopup, for a caller that waits on the popup: it opens once a preview already open has closed.
	openPreviewAndGetPopup(
		report: Report,
		origin: TrackingOriginTypeForPreview = 'page',
	): Promise<PopupModal> {
		this.analyticsService.event(TrackingEvent.REPORT_SHOW_PREVIEW, {
			origin,
			report_id: report.numericId,
		});

		let beforeOpen: Promise<unknown>;
		const existingPreviewPopup = this.popupService.findByComponent(
			ReportsPopupPreviewComponent,
		);
		if (existingPreviewPopup) {
			// if already open, close the previous and open a new one
			beforeOpen = existingPreviewPopup.close();
		} else {
			beforeOpen = Promise.resolve();
		}

		return beforeOpen.then(() =>
			this.createAndOpenPreviewPopup({
				report,
				trackingOrigin: origin,
			}),
		);
	}

	isReportPreviewPopupOpen() {
		return this.popupService.isOpen(ReportsPopupPreviewComponent);
	}

	createAndOpenPreviewPopup(props: PopupData.Report.Preview.Any) {
		const popup = this.popupService.createModal(ReportsPopupPreviewComponent, {
			popupClass: 'popup-type-board-preview report wall',
			isAutoCentered: false,
			isAnimated: false,
			allowStacked: true, // more context, share, tour bubbles, ..
		});

		popup.open(props);

		return popup;
	}

	openCoverPopup(
		onFinish: PopupData.Report.Cover['onFinish'],
		blobUrl = '',
		existingUrl = '',
		action: CoverAction,
		crop?: ImageCropData | null,
		imageAspect?: number,
	) {
		if (this.accountService.handleDemo()) {
			return;
		}

		const popup = this.popupService.createModal(ReportsPopupCoverComponent, {
			popupClass: 'popup-type-report-cover',
		});

		const popupData: PopupData.Report.Cover = {
			onFinish: (url, originalUrl, crop) => {
				onFinish(url, originalUrl, crop);
			},
			action,
		};

		if (blobUrl) {
			popupData.imageBlobUrl = blobUrl;
		}
		if (existingUrl) {
			popupData.imageOriginalUrl = existingUrl;
		}
		if (crop) {
			popupData.imageCrop = crop;
		}
		if (imageAspect) {
			popupData.imageAspect = imageAspect;
		}

		popup.open(popupData);

		return popup;
	}

	createAndOpenNewNotificationForModel(
		model: Report,
		trackingOrigin: TrackingOriginTypeForSchedulePopup,
		isReminder = false,
	) {
		this.mmNotificationsService.createNewForFeature(
			isReminder ? NotificationCreateType.REMINDER : NotificationCreateType.REPORT,
			model,
			trackingOrigin,
		);
	}

	openSharePopup(
		report: Report,
		origin: TrackingOriginTypeForShare = 'page',
		selectedTab?: ReportPopupShareTab,
	): void {
		if (this.accountService.handleDemo()) {
			return;
		}

		this.analyticsService.event(TrackingEvent.REPORT_SHARE_POPUP_OPEN, { origin });

		this.popupService
			.createModal(ReportsPopupShareComponent, {
				popupClass: 'popup-type-report-share title-bigger',
				allowStacked: true, // used because of: report/schedules/popup-send-later (send to email tab)
			})
			.open({
				model: report,
				selectedTab,
				trackingOrigin: origin,
			});
	}

	openAccountTemplateEditPopup(
		model: Report | null,
		trackingOrigin: TrackingOriginForTemplateCreated,
	): void {
		if (this.accountService.handleDemo()) {
			return;
		}

		const modelCopy = model ? this.createModelFromRecord(model.model) : null;
		this.mmTemplatesService.openAccountTemplateEditPopup(
			TemplateObjectType.REPORT,
			modelCopy,
			trackingOrigin,
		);
	}

	openPublicTemplateEditPopup(report: Report): void {
		if (this.accountService.handleDemo()) {
			return;
		}

		// make sure templates are loaded
		void this.mmTemplatesService.requestReportPublicTemplates().then(() => {
			this.mmTemplatesService.openPublicTemplateEditPopup(report);
		});
	}

	duplicateReport(report: Report): Promise<Report> | null {
		if (this.accountService.handleDemo()) {
			return Promise.reject();
		}

		if (!this.billingService.checkIfCan(BillingFeature.REPORTS_BASIC, true)) {
			return null;
		}

		const layer = this.infolayerService.loading('Duplicating report...');

		return new Promise((resolve, reject) => {
			this.requestService
				.api<APIData.Report.RecordResponse>('POST', `/reports/${report.id}/clone`, null, {
					timeout: 60 * 1000,
				})
				.then(
					(reply) => {
						layer.ok(`Report <strong>${report.title}</strong> duplicated!`);
						this.storeService.pushPayload(reply);

						const createdRecordId = reply.report?.id;
						let createdReport;
						if (createdRecordId) {
							createdReport = this.findModel(String(createdRecordId));
						}

						if (createdReport) {
							createdReport.model.newBadge = true;
							resolve(createdReport);
						} else {
							reject();
						}
					},
					(errorData) => {
						reject();
						layer.close();
						throw errorData;
					},
				)
				.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
		});
	}

	createFromBoards(
		items: Board | Board[],
		trackingOrigin: TrackingOriginTypeForAddReport | null,
	): void {
		if (!this.billingService.checkIfCan(BillingFeature.REPORTS_BASIC, true)) {
			return;
		}

		const layer = this.infolayerService.loading('Creating report...');
		const report = this.createNewModel({ title: DEFAULT_REPORT_TITLE });
		const isArray = Array.isArray(items);

		void report.save().then((reportRecord) => {
			if (isArray) {
				const reportId = +reportRecord.id!;
				this.mmBoardsService
					.batchAddToReport(items, reportId)
					.then(() => {
						layer.ok('Report created!');

						if (trackingOrigin) {
							this.triggerReportEdited(trackingOrigin);
						}

						const targetReportModel = this.findModel(reportRecord.id!)!;
						this.navigationService.createContextSwitchModal().open({
							actionType: ContextSwitchAction.ADD_TO,
							sourceItem: items,
							targetItem: targetReportModel,
							onSwitch: () => {
								this.navigationService.transitionToRoute(
									'reports.editor.report',
									reportId,
								);
							},
						});
					})
					.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
			} else {
				this.mmReportSlidesService
					.createFromBoard(items.numericId!, +reportRecord.id!)
					.then(
						() => {
							this.analyticsService.event(TrackingEvent.REPORT_CREATED, {
								origin: trackingOrigin,
							});
							layer.ok('Report from dashboard created!');
							this.editReport(report);
						},
						(errorData) => {
							layer.close();
							throw errorData;
						},
					)
					.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
			}
		});
	}

	createFromLoop(loop: BoardLoop, trackingOrigin: TrackingOriginTypeForCreate): void {
		if (this.accountService.handleDemo()) {
			return;
		}

		const layer = this.infolayerService.loading('Creating report...');

		this.requestService
			.api<APIData.Report.RecordResponse>('POST', '/reports', {
				boardLoopId: loop.numericId,
			})
			.then(
				(reply) => {
					layer.ok('Report from looped dashboard created!');
					this.analyticsService.event(TrackingEvent.REPORT_CREATED, {
						origin: trackingOrigin,
					});
					this.storeService.pushPayload(reply);

					const createdRecordId = reply.report?.id;
					if (createdRecordId) {
						const createdRecord = this.findModel(String(createdRecordId));
						if (createdRecord) {
							this.editReport(createdRecord);
						}
					}
				},
				(errorData) => {
					layer.close();
					throw errorData;
				},
			)
			.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
	}

	checkIfCanUseTemplate(
		isPublicTemplate: boolean,
		openWarning = false,
		boardsCount?: number,
	): boolean {
		let actionPassesLimitations = false;

		if (this.billingService.checkIfCan(BillingFeature.REPORTS_BASIC, openWarning)) {
			if (
				isPublicTemplate ||
				this.billingService.checkIfCan(BillingFeature.ACCOUNT_TEMPLATES, openWarning)
			) {
				actionPassesLimitations = this.billingService.checkIfCan(
					BillingFeature.BOARDS,
					openWarning,
					boardsCount,
				);
			}
		}

		return actionPassesLimitations;
	}

	createReportFromTemplate(
		templateModel: Template,
		trackOrigin = '',
		afterCreated?: (report: ReportRecord, boardsData: RecordData.Board.Raw[]) => void,
		onResolveModalClose?: () => void,
		sourcesMap: SourcesMap = {},
		skipResolvePopup = false,
		{
			isInfolayerShown = true,
			isTrackingDisabled = false,
		}: {
			isInfolayerShown?: boolean;
			isTrackingDisabled?: boolean;
		} = {},
	): void {
		if (this.accountService.handleDemo()) {
			return;
		}

		const onAllResolved = (data?: TemplateResolve.NormalizedResolvePayload) => {
			const fromId = templateModel.numericId!;
			let layer: InfolayerItemGeneral | null = null;

			if (isInfolayerShown) {
				layer = this.infolayerService.loading('Building report...');
			}

			this.mmTemplatesService
				.useTemplate(
					templateModel,
					data ?? { dataSourceTypeMap: {} },
					trackOrigin as TrackingOriginForTemplateUsed,
				)
				.then(
					(r) => {
						const reply = r as APIData.Templates.UseReportTemplateResponse,
							boardsData = reply.boards,
							reportData = reply.reports?.[0];

						if (reportData) {
							const reportRecord = this.savedRecords.find(
								(item) => item.id === String(reportData.id),
							);

							if (layer) {
								showTemplateCreationResult(layer, reply, 'Report');
							}

							if (reportRecord && afterCreated) {
								reportRecord.newBadge = true;

								if (!isTrackingDisabled) {
									this.analyticsService.event(TrackingEvent.REPORT_CREATED, {
										origin: trackOrigin,
									});
								}

								(boardsData || []).forEach((board) => {
									const boardModel = this.mmBoardsService.normalBoardModels.find(
										(item) => item.id === String(board.id),
									);

									if (boardModel) {
										boardModel.model.newBadge = true;

										if (!isTrackingDisabled) {
											const from =
												trackOrigin === 'sidebar'
													? 'sidebar_report'
													: trackOrigin;

											this.mmBoardsService.triggerDataboardCreated(
												{ from },
												boardModel,
											);
										}
									}
								});

								afterCreated(reportRecord, boardsData);
							}
						} else {
							if (layer) {
								layer.error('Error building report');
							}
							this.loggerService.logError(
								'[bgEvent] create report',
								{
									fromId,
									response: JSON.stringify(reply),
								},
								Severity.INFO,
							);
						}
					},
					(errorData) => {
						if (layer) {
							layer.close();
						}

						throw errorData;
					},
				)
				.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
		};

		if (this.checkIfCanUseTemplate(templateModel.isPublic, true)) {
			if (skipResolvePopup) {
				onAllResolved({ dataSourceTypeMap: sourcesMap });
			} else {
				this.mmTemplatesService.openResolvePopup(
					templateModel,
					onAllResolved,
					trackOrigin,
					onResolveModalClose,
				);
			}
		}
	}

	afterCreateReportFromTemplate = (reportRecord: ReportRecord) => {
		const afterTransition = () => {
			const reportModel = this.createModel(reportRecord);
			this.openPreviewPopup(reportModel);
		};

		this.navigationService.transitionToRoute('reports.index').then(afterTransition);
	};

	deleteReport(report: Report, permanently = false, cb?: () => number): Promise<void> {
		if (this.accountService.handleDemo()) {
			return Promise.reject();
		}

		return new Promise((resolve, reject) => {
			const affirmativeFunc = () => {
				const id = report.id;
				const infolayer = this.infolayerService.loading('Deleting report...');

				this.requestService
					.api<RecordData.Payload>('DELETE', `/reports/${id}`)
					.then(
						(reply) => {
							this.analyticsService.event(TrackingEvent.REPORT_DELETED);

							if (id) {
								this.markBoardNotRequested(id);
								this.garbageCollectorService.clearConnectedRecords('report', {
									id,
								});
							}

							let waitBeforeRemove = 0;

							if (typeof cb === 'function') {
								waitBeforeRemove = cb();
							}

							later(() => {
								void report.delete(true);
								this.storeService.pushPayload(reply);
							}, waitBeforeRemove);

							infolayer.ok(`Report <strong>${report.title}</strong> deleted!`);
							resolve();
						},
						(errorData) => {
							infolayer.close();
							throw errorData;
						},
					)
					.catch((err: RequestError) => {
						this.requestService.handleGenericThrownError(err);
						reject();
					});
			};

			this.popupService.openConfirmPopup({
				onConfirm: affirmativeFunc,
				title: `Are you sure you want to${permanently ? ' permanently' : ''} delete the report "${report.title}"?`,
				text: permanently
					? '<strong>This cannot be undone.</strong>'
					: `<strong>"${report.title}" will be moved to Recently deleted and deleted permanently after 30 days.</strong><br><br>Note that this report will be removed from all Home widgets, and all related notifications will be deleted. These will not be restored even if you restore the report.<br><br>`,
				confirmAction: 'Delete',
				cancelAction: 'Cancel',
			});
		});
	}

	async toggleVerification(report: Report): Promise<void> {
		const verificationInfo = report.verificationInfo;
		if (!verificationInfo) return;
		await this.verificationService.toggleVerification(
			verificationInfo,
			'report',
			report.title,
			(updated) => {
				report.directPropUpdate({ verificationInfo: updated });
			},
		);
	}

	triggerReportEdited(
		trackingOrigin:
			TrackingOriginTypeForReportEdited | TrackingOriginTypeForAddReport = 'editor',
	): void {
		this.analyticsService.event(TrackingEvent.REPORT_EDITED, {
			origin: trackingOrigin,
		});
	}

	reorderSlides(report: Report, slides = report.sortedSlides): ReportSlide[] {
		const order = slides.map((item) => item.numericId) as number[];

		this.mmReportSlidesService.commitReorderForReport(report, order);

		this.triggerReportEdited();

		return slides;
	}

	openCreateBlockPopup(data: PopupData.Report.CreateBlock) {
		const popup = this.popupService.createModal(ReportsPopupCreateBlockComponent, {
			popupClass: 'popup-type-report-create-block',
			allowStacked: true, // metric-select, blocktype picker
		});
		popup.open(data);
		return popup;
	}

	openInAppFullscreen(model: Report, inPresentMode = false) {
		const queryParams = inPresentMode ? { present: true } : {};
		return this.navigationService.transitionToRoute('reports.view.report', model, {
			queryParams,
		});
	}

	showReportsTourIntroPopup(): void {
		const popup = this.popupService.createModal(ReportsPopupTourIntroComponent, {
			popupClass: 'popup-reports-tour',
			showClose: true,
			isAutoCentered: true,
		});

		popup.open();
	}

	triggerOuterSlideResize(): void {
		schedule('afterRender', () => {
			later(() => {
				window.dispatchEvent(new Event('resize'));
			}, 100);
		});
	}

	triggerInnerSlideResize(): void {
		schedule('afterRender', () => {
			later(() => {
				$(window).trigger('resize.datawallcenter');
			}, 100);
		});
	}

	// ------ Present mode ------

	startPresent(report: Report, origin: TrackingOriginForPresent) {
		// in-app fullscreen view w/ present param, browser fullscreen
		this.initializePresentMode(report, origin, true);
	}

	startPresentWithNotes(report: Report, origin: TrackingOriginForPresent) {
		// in-app fullscreen view w/ present param, extra popup window
		this.initializePresentMode(report, origin);
	}

	private initializePresentMode(
		report: Report,
		origin: TrackingOriginForPresent,
		isFullscreen = false,
	) {
		const enteringTransition = this.openInAppFullscreen(report, true);
		enteringTransition.then((route: ReportsViewReportRoute) => {
			const controller = route.controller as ReportsViewReportController;
			controller.enteringTransition = enteringTransition;

			// after activate has been called, and after blockrenderer has been initialized
			// if we do it too soon, block content elements will subscribe events to a renderer that will be destroyed
			// and the new renderer will not get any events triggered
			next(() => {
				schedule('afterRender', () => {
					next(() => {
						if (isFullscreen) {
							controller.startPresent(origin);
						} else {
							controller.startPresentWithNotes(origin);
						}
					});
				});
			});
		});
	}

	// ------ Requests ------

	_reportsRequested = false;
	_reportsRequestPromise: Promise<void> | null = null;
	requestReports(): Promise<void> {
		const variablePrefix = 'reports';
		return this.requestService.dataRequestOnce<typeof variablePrefix>(
			this,
			variablePrefix,
			() => {
				// const testData = window.dbx_mock['GET /d/reports'];
				// return Promise.resolve(testData);

				return this.requestService.storeFindAll(ModelName.REPORT);
			},
		);
	}

	// ---------- requested reports cache START ------------

	maxReportIdsToRemember = 3;

	@tracked reportsRequested: RequestedReportData[] = [];
	@tracked reportsRequestingData: { id: string; promise: Promise<unknown> }[] = [];

	isReportDataRequested(id: string) {
		return !!id && this.reportsRequested.some((requestedReport) => requestedReport.id === id);
	}

	isReportDataRequesting(id: string) {
		return !!id && !!this.reportsRequestingData.find((r) => r.id === id);
	}

	requestReportData(id: string) {
		let promise;

		// first check if already requested
		if (this.isReportDataRequested(id)) {
			// we update the requested timestamp, that will be used for sorting out the oldest report
			const indexToUpdate = this.reportsRequested.findIndex(
					(requestedReport) => requestedReport.id === id,
				),
				updatedRequestedBoard: RequestedReportData = {
					id,
					requestedTimestamp: Date.now(),
				};

			if (indexToUpdate !== -1) {
				// update the object at the found index with the new data
				this.reportsRequested[indexToUpdate] = updatedRequestedBoard;
			}

			promise = Promise.resolve();
		} else {
			// check if requesting is in progress
			promise = this.reportsRequestingData.find((r) => r.id === id)?.promise;

			if (!promise) {
				// this request returns only blocks and metricsettings (not samples)
				promise = this.requestService
					.api<RecordData.Payload>('GET', `/reports/${id}`)
					.then((reply) => {
						this.markReportRequested(id);
						this.storeService.pushPayload(reply);
					})
					.finally(() => {
						this.reportsRequestingData = this.reportsRequestingData.filter(
							(r) => r.id !== id,
						);
					})
					.catch(this.requestService.handleGenericThrownError.bind(this.requestService));

				this.reportsRequestingData = [...this.reportsRequestingData, { id, promise }];
			}
		}

		return promise;
	}

	markReportRequested(id: string) {
		if (!id) {
			return;
		}

		this.reportsRequested = [
			...this.reportsRequested,
			{
				id,
				requestedTimestamp: Date.now(),
			},
		];

		// check if we should unload some report data (perf reasons)
		if (this.reportsRequested.length > this.maxReportIdsToRemember) {
			const oldestRequestedReport = this.reportsRequested.reduce(
				(oldestRequestedReport: RequestedReportData, requestedReport) => {
					if (
						requestedReport.requestedTimestamp <
						oldestRequestedReport.requestedTimestamp
					) {
						return requestedReport; // update oldestRequestedReport to the current object if its timestamp is smaller
					}
					return oldestRequestedReport; // otherwise, keep the current oldestRequestedReport
				},
				this.reportsRequested[0]!,
			); // initialize with the first object

			this.unloadRequestedReport(oldestRequestedReport.id);
		}
	}

	markBoardNotRequested(id: string) {
		if (!id) {
			return;
		}

		this.removeIdFromReportsRequested(id);
	}

	unloadRequestedReport(id: string) {
		const garbageCollector = this.garbageCollectorService;
		if (garbageCollector) {
			this.removeIdFromReportsRequested(id);
			garbageCollector.clearConnectedRecords('report', { id });
		}
	}

	private removeIdFromReportsRequested(id: string) {
		this.reportsRequested = this.reportsRequested.filter(
			(requestedReport) => requestedReport.id !== id,
		);
	}

	@tracked isRecentlyDeletedLoading = false;
	requestDeletedReports() {
		this.isRecentlyDeletedLoading = true;
		return this.requestService
			.api<APIData.Reports.RecentlyDeleted.Response>('GET', '/reports', {
				onlyDeleted: true,
			})
			.then((response) => {
				this.storeService.pushPayload(response);
			})
			.catch(this.requestService.handleGenericThrownError.bind(this.requestService))
			.finally(() => {
				this.isRecentlyDeletedLoading = false;
			});
	}

	restore(model: Report) {
		if (!this.billingService.checkIfCan(BillingFeature.REPORTS_BASIC, true)) {
			return;
		}

		return this.requestService
			.api<APIData.Reports.Restore.Response>('POST', '/reports/restore', {
				reportId: model.id,
			})
			.then((response) => {
				this.storeService.pushPayload(response);
				this.infolayerService.ok(
					`<strong>${model.title}</strong> report successfully restored.`,
				);
			})
			.catch(this.requestService.handleGenericThrownError.bind(this.requestService));
	}

	// ---------- requested reports cache END ------------

	requestActivityLogReports(params?: {
		preStoreFn?: () => Promise<void>;
		queryParams?: Record<string, unknown>;
	}): Promise<RecordData.Payload> {
		return this.requestService.api<RecordData.Payload>('GET', '/reports', params?.queryParams);
	}

	downloadSnapshotWithInfolayer(
		report: Report,
		password = '',
		includeBgImages = false,
		layout?: ReportLayout,
	) {
		const infolayer = this.infolayerService.reportPdf({
			reportId: report.numericId,
			title: report.title,
		});

		const payload: APIData.Report.SnapshootRequest = {
			includeBgImages,
			layout: layout || report.layout,
		};
		if (password) {
			payload.password = password;
		}

		return this.requestService
			.apiWithAsyncReply('POST', `/reports/${report.id}/snapshoot`, payload, {
				timeout: 5 * 60 * 1000,
			})
			.then((reply) => {
				// reply = { reportDeliveries: [{id: 123, title: 'Something', reportUrl: 'https://dashboardsnapshotsstage.s3.amazonaws.com/Story%20%202022-07-04%2010.10am%209ln2qpfa69c.pdf', size: 69073 }] };
				const deliveryId = reply?.reportDeliveries?.[0]?.id;
				if (deliveryId) {
					const reportDelivery = this.mmReportDeliveriesService.findModel(
						String(deliveryId),
					);
					if (reportDelivery) {
						return Promise.resolve(reportDelivery);
					}
				}
				return Promise.reject();
			})
			.then((reportDelivery) => {
				infolayer.setDone(reportDelivery);
			})
			.catch((e: RequestError) => {
				infolayer.setError('Error generating report');
				this.requestService.handleGenericThrownError(e);
			});
	}

	applyVisualSettingsToAllSlides(
		report: Report,
		options: {
			color: string;
			themeId: number;
			logo?: string;
			logoOriginal?: string;
			logoCrop?: ImageCropData | null;
			bg?: string;
			bgOriginal?: string;
			bgCrop?: ImageCropData | null;
			bgOpacity?: number;
		},
	): void {
		if (!report) {
			return;
		}

		const slidesTypeNotBoard = report.slidesTypeNotBoard,
			firstSlideId = slidesTypeNotBoard[0]?.id;

		if (!firstSlideId) {
			return;
		}

		this.requestService
			.api('PUT', `/report-slides/${firstSlideId}/batch`, options)
			.then(() => {
				slidesTypeNotBoard.forEach((slide) => {
					const props: {
						color: string;
						themeId: number;
						logo?: string;
						logoOriginal?: string;
						logoCrop?: ImageCropData | null;
						bg?: string;
						bgOriginal?: string;
						bgCrop?: ImageCropData | null;
						bgOpacity?: number;
					} = {
						color: options.color,
						themeId: options.themeId,
					};

					if (options.logo !== undefined) {
						props.logo = options.logo;
						props.logoOriginal = options.logoOriginal || '';
						props.logoCrop = options.logoCrop || null;
					}

					if (options.bg !== undefined) {
						props.bg = options.bg;
						props.bgOriginal = options.bgOriginal || '';
						props.bgCrop = options.bgCrop || null;
					}

					if (options.bgOpacity !== undefined) {
						props.bgOpacity = options.bgOpacity;
					}

					slide.directPropUpdate(props);
				});
			})
			.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
	}

	createBlockForReport(report: Report, block: Block): Promise<BlockRecord> {
		let queryParams = '';

		if (report.isTemplate) {
			queryParams = '?isFromTemplate=true';
		}

		return this.requestService
			.api<APIData.Reports.CreateBlockForReportResponse>('POST', `/blocks${queryParams}`, {
				block: block.toSerializedJSON(),
			})
			.then((response) => {
				return new Promise((resolve, reject) => {
					this.storeService.pushPayload(response);

					//next(() => { // To prevent throwing error "it had already been used previously in the same computation" locally
					const blockRecord =
						this.mmBlocksService.findRecord(String(response.block.id)) || null;

					if (blockRecord) {
						resolve(blockRecord);
					} else {
						reject({
							code: 422,
							errors: [
								{
									code: 422,
									name: 'general',
									message: 'There was some problem while creating block.',
								},
							],
						});
					}
					//});
				});
			});
	}

	loadFonts(): Promise<void> {
		return new Promise((resolve) => {
			if (this.isFontLoaded) {
				return resolve();
			}

			const onDone = () => {
				this.isFontLoaded = true;
				return resolve();
			};

			// will trigger special event when previewdata font is loaded
			// because then we need to reposition / recalculate graphs etc items (as they're pixel-dependant)
			try {
				let stillLoadingCount = 0; // keeping count bcos sometimes active callback does not trigger (snapshooter)
				WebFont.load({
					google: {
						families: [
							//'Roboto:italic,400,700', // loaded in app-loaded-events-with-font
							'Roboto+Condensed:400,700',
							'Roboto+Slab:italic,400,700',
							'EB+Garamond:italic,400,700',
							'Spline+Sans+Mono:italic,400,700',
							'Open+Sans:italic,400,700',
							'Fira+Sans:italic,400,700',
							'Nunito:italic,400,700',
							'Merriweather:italic,400,700',
							'Gelasio:italic,400,700',
							'Playfair+Display:italic,400,700',
							'Courier+Prime:italic,400,700',
							'Caveat:italic,400,700',
							'Lobster:italic,400,700',
						],
					},
					active: () => {
						return onDone();
					},
					inactive: () => {
						return onDone();
					},
					fontloading: () => {
						stillLoadingCount++;
					},
					fontactive: () => {
						stillLoadingCount--;
						if (!stillLoadingCount) {
							return onDone();
						}
					},
					fontinactive: () => {
						stillLoadingCount--;
						if (!stillLoadingCount) {
							return onDone();
						}
					},
				});
			} catch (e) {
				this.loggerService.logError('Reports: WebFont error', e as object, Severity.ERROR);
				this.isFontLoaded = true;
				return resolve();
			}
		});
	}

	// ------ Batch actions ------

	override batchFavorite(items: Report[]) {
		return super.batchFavorite(items).then(() => {
			return this.updateOrderAfterFavoriteToggle();
		});
	}

	override batchUnfavorite(items: Report[]) {
		return super.batchUnfavorite(items).then(() => {
			return this.updateOrderAfterFavoriteToggle();
		});
	}

	updateOrderAfterFavoriteToggle(): Promise<void> {
		const allReports = sortBy(this.normalReportModels, 'order', 'numericId');
		const favorites: Report[] = [];
		const nonFavorites: Report[] = [];

		allReports.forEach((report: Report) => {
			if (report.favorite) {
				favorites.push(report);
			} else {
				nonFavorites.push(report);
			}
		});

		const newOrderedReports = [...favorites, ...nonFavorites];
		const newOrderIds = newOrderedReports.map((report) => report.numericId!);

		newOrderedReports.forEach((report, index) => {
			report.order = index;
			report.applyBufferedChanges(['order']);
		});

		return this.requestService
			.api<RecordData.Payload>('POST', '/reports/order', newOrderIds)
			.then((reply) => {
				this.storeService.pushPayload(reply);
			})
			.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
	}

	batchDeletePermanently(items: Report[]) {
		return super.batchDelete(items);
	}

	batchRestore(items: Report[]) {
		return super.batchRestoreAction(items).then(() => {
			items.forEach((item) => {
				item.deleted = null;
			});
		});
	}

	// ------ For-store ------

	modelName = ModelName.REPORT;

	createModel(model: ReportRecord): Report {
		return new Report({ model });
	}

	override createNewModel(properties: CreateRecordProperties<ReportRecord> = {}): Report {
		if (typeof properties.ownerId === 'undefined') {
			properties.ownerId = this.currentUserService.numericId ?? undefined;
		}
		if (typeof properties.title === 'undefined') {
			properties.title = 'My dashboard presentation';
		}
		if (typeof properties.order === 'undefined') {
			properties.order = this.savedRecords.length;
		}
		return super.createNewModel(properties);
	}

	// ------ Report tracking helpers ------

	@tracked reportCreationOrigin: TrackingOriginTypeForCreate | null = null;

	setReportCreationOrigin(origin: TrackingOriginTypeForCreate): void {
		this.reportCreationOrigin = origin;
	}

	getAndClearReportCreationOrigin(): TrackingOriginTypeForCreate | null {
		const origin = this.reportCreationOrigin;
		this.reportCreationOrigin = null;
		return origin;
	}
}

declare module '@ember/service' {
	interface Registry {
		'model-manager/reports': ModelManagerReportsService;
	}
}
